import React from 'react'
import { graphql } from 'gatsby'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import Loadable from '@loadable/component'

import { ActionButton, Footer, FormField, GtmDataLayerSetup, HeaderBanner, Link, Title } from '@jelly/components'
import { makeImgResizerUrl } from '@jelly/utils'
import Layout from 'common/LayoutNoHero'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

const Form = Loadable(() => import(/* webpackChunkName: 'rff' */ 'react-final-form').then(({ Form }) => Form))

const Container = styled.div`
	${({ theme }) => theme.background.secondary};
`

const Text = styled.p`
	${({ theme, variant }) => theme.text[variant]};
`
Text.defaultProps = {
	variant: 'bodySmall',
}

const HeaderBannerWrap = styled.div`
	background: ${({ theme }) => theme.colors.lightGrey};
	width: 100%;
	border-bottom: ${({ theme }) => `thin solid ${theme.colors.navy}`};
`
// Styled Components via Emotion
const BodyContainer = styled.div`
	height: 100%;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		max-width: 60vw;
		width: 90%;
		min-height: 70vh;
		margin: auto;
		margin-top: 10px;
		margin-bottom: 30px;
	}
`
const Tile = styled.div`
	background-color: ${({ theme }) => theme.colors.white};
	padding: 1rem;
	margin-bottom: 1rem;
	box-shadow: ${({ theme }) => theme.shadows.small};
`

const FormWrapper = styled.form`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	> * {
		margin: 1rem;
	}
`
const Card = styled.div`
	display: flex;
	background-color: ${({ theme }) => theme.colors.white};
	box-shadow: ${({ theme }) => theme.shadows.small};
	margin: 2rem 0;
	padding: 1rem;
`
const Col = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: space-between;
	> img {
		margin-right: 1rem;
	}
	> p,
	> a {
		margin-top: 0;
		margin-bottom: 1rem;
	}
`
const Article = ({ slug, author, canonical, published, stream, thumbnail, title }) => {
	return (
		<Card>
			<Col>
				<img src={makeImgResizerUrl({ method: 'rc', src: thumbnail.path, w: 150, h: 130 })} alt={slug} />
			</Col>
			<Col>
				<Link variant="default" href={`${canonical}?source=404`}>
					{title}
				</Link>
				<Text variant="bodySmall">{stream}</Text>
				<Text>
					{`By ${author.displayName}`}
					&nbsp;-&nbsp;{dayjs().to(dayjs(published))}
				</Text>
			</Col>
		</Card>
	)
}

const NotFoundPage = ({ message = 'Sorry, we made a mistake.', data: { articles } = {} }) => {
	return (
		<Layout meta={{ title: 'Page not found - businessnewsdaily.com' }}>
			<Container>
				<GtmDataLayerSetup contentType="404" templateType="" />
				<HeaderBannerWrap>
					<HeaderBanner />
				</HeaderBannerWrap>
				<BodyContainer>
					<Tile>
						<div class="tile-content copy">
							<div class="block pb2">
								<Title variant="sectionHeaderLarge">{message}</Title>
								<Text>
									We messed something up. That’s why you’re seeing this page. It’s called a 404 page.
								</Text>
								<Text>
									If you have an online business or personal website, your site probably has a page
									like this, too. But your 404 page doesn't need to be a dead end for your customers.
									Instead, you can turn digital lemons into lemonade by making the best out of a bad
									user experience. Here is some advice on how to craft a user-friendly 404 page.
								</Text>
								<Title variant="sectionHeaderSmall">Be Clear</Title>
								<Text>Make sure people know something went wrong.</Text>
								<Title variant="sectionHeaderSmall">Be Apologetic</Title>
								<Text>This is our bad. You probably clicked a bum link. Our fault, not yours.</Text>
								<Title variant="sectionHeaderSmall">Be Helpful</Title>
								<Text>An apology means a lot more when a person tries to make things right.</Text>
								<Title variant="sectionHeaderSmall">Fix the Problem</Title>
								<Text>
									We just took note of the page you came from and where you were trying to go. Now we
									can fix our mistake.
								</Text>
							</div>
						</div>
					</Tile>
					<Tile>
						<Title variant="sectionHeaderSmall">What can we help you find?</Title>
						<Form
							onSubmit={({ q }) => {
								window.location.href = `/search?q=${q}`
							}}
							render={({ handleSubmit, form, submitting, pristine, values }) => (
								<FormWrapper onSubmit={handleSubmit}>
									<FormField type="text" name="q" required />
									<ActionButton variant="small" type="submit">
										Submit
									</ActionButton>
								</FormWrapper>
							)}
						/>
					</Tile>
					{articles && <Title variant="sectionHeaderSmall">Latest Content:</Title>}
					{articles && articles.map((article) => <Article key={article.slug} {...article} />)}
				</BodyContainer>
				<Footer />
			</Container>
		</Layout>
	)
}

export const GatsbyQuery = graphql`
	{
		articles(limit: 10, sort: { published: DESC }) {
			author {
				displayName
				slug
				user {
					slug
				}
			}
			canonical
			published
			subtitle
			stream
			thumbnail {
				path
				caption
			}
			title
		}
	}
`

export default NotFoundPage
